import { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  loginApi, registerApi, meApi, logoutApi, joinApi, restoreApi, clearCustomerClientState,
  getCustomerToken, setCustomerToken, removeCustomerToken, setActiveToken,
} from "@/api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const t = localStorage.getItem("bs_token");
      if (!t) { setLoading(false); return; }
      try {
        const { user } = await meApi();
        // Legacy migration: mirror the single global token into its business-scoped
        // slot so a returning device keeps its card without ever forcing a re-login.
        if (user && user.role === "customer" && user.business_id) {
          setCustomerToken(user.business_id, t);
        }
        setUser(user);
      } catch (e) {
        await clearCustomerClientState();
        setUser(null);
      }
      setLoading(false);
    })();
  }, []);

  const login = useCallback(async (email, password) => {
    const { token, user } = await loginApi(email, password);
    localStorage.setItem("bs_token", token);
    setUser(user);
    return user;
  }, []);

  const register = useCallback(async (name, email, password) => {
    const { token, user } = await registerApi(name, email, password);
    localStorage.setItem("bs_token", token);
    setUser(user);
    return user;
  }, []);

  const joinCustomer = useCallback(async (slug, name) => {
    const { token, user, recovery_code } = await joinApi(slug, name);
    setActiveToken(token);
    if (user && user.business_id) setCustomerToken(user.business_id, token);
    localStorage.setItem("bs_slug", slug);
    try { if (recovery_code) localStorage.setItem(`bs_recovery:${slug}`, recovery_code); } catch (e) { /* noop */ }
    setUser(user);
    return { user, recovery_code };
  }, []);

  const restoreCustomer = useCallback(async (code) => {
    const { token, user } = await restoreApi(code);
    setActiveToken(token);
    if (user && user.business_id) setCustomerToken(user.business_id, token);
    setUser(user);
    return user;
  }, []);

  // Open a specific business's remembered card. Reads that business's scoped token,
  // makes it active, and validates it server-side. Returns the customer user only
  // when the token genuinely belongs to THIS business; otherwise cleans up and
  // returns null (→ caller shows onboarding). Never touches other businesses' cards.
  const activateBusinessCard = useCallback(async (business) => {
    if (!business || !business.id) return null;
    const token = getCustomerToken(business.id);
    if (!token) return null;
    setActiveToken(token);
    try {
      const { user } = await meApi();
      if (user && user.role === "customer" && user.business_id === business.id) {
        setCustomerToken(business.id, token);
        try { if (business.slug) localStorage.setItem("bs_slug", business.slug); } catch (e) { /* noop */ }
        setUser(user);
        return user;
      }
    } catch (e) { /* invalid / expired token */ }
    removeCustomerToken(business.id);
    setActiveToken(null);
    return null;
  }, []);

  const refreshUser = useCallback(async () => {
    const { user } = await meApi();
    setUser(user);
    return user;
  }, []);

  const logout = useCallback(async (businessId) => {
    try { await logoutApi(); } catch (e) { /* stateless */ }
    // Forget ONLY this business's remembered card; keep other businesses intact.
    if (businessId) removeCustomerToken(businessId);
    setActiveToken(null);
    setUser(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, joinCustomer, restoreCustomer, activateBusinessCard, refreshUser, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}
